import {
  type ClipCutout,
  createTextElement,
  cutoutTrack,
  insertElements,
  isBehindPersonTrack,
  layoutClips,
  type OverlayClip,
  overlayClipEnd,
  placeBehindPerson,
  resolveElementTracks,
  type SceneDocument,
  type VideoClip,
} from "@cva/shared";
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { client, orpc } from "../../lib/api";
import { errorMessage } from "../../lib/utils";
import type { AssetInfo } from "../commands";
import type { EditorStore } from "../store";

/**
 * "Person freistellen" in the video editor (docs/05-video-und-untertitel.md):
 * a clip gets `cutout`, the server cuts the person out of its video once, and
 * from then on the person is its own layer, the person track: what lies on
 * the tracks below it (titles, pictures, videos) is behind the person. While
 * the server works, the clip plays as before.
 */

export type ClipRef = { track: "video" | "overlay"; id: string };

function findClip(doc: SceneDocument, ref: ClipRef): VideoClip | OverlayClip | undefined {
  return ref.track === "video"
    ? doc.timeline?.clips.find((clip) => clip.id === ref.id)
    : doc.timeline?.overlays?.find((clip) => clip.id === ref.id);
}

/** Whether a clip can get a cut-out person (videos, not in a device). */
export function canCutOutPerson(clip: VideoClip | OverlayClip | undefined): boolean {
  return Boolean(clip && clip.kind === "video" && !("device" in clip && clip.device));
}

/** Sets or removes the cut-out person of a clip (one undoable step). */
export function setClipCutout(store: EditorStore, ref: ClipRef, cutout: ClipCutout | null) {
  store.getState().update((draft) => {
    const clip = findClip(draft, ref);
    if (!clip) return;
    if (cutout) clip.cutout = cutout;
    else delete clip.cutout;
  });
}

/** Starts cutting the person out of the clip's video (once per video) and turns it on for the clip. */
export async function cutOutPerson(options: {
  orgId: string;
  store: EditorStore;
  queryClient: QueryClient;
  ref: ClipRef;
  asset: AssetInfo | undefined;
  background?: ClipCutout["background"];
}): Promise<void> {
  const { orgId, store, queryClient, ref, asset } = options;
  const clip = findClip(store.getState().doc, ref);
  if (!clip || !asset || !canCutOutPerson(clip)) return;
  setClipCutout(store, ref, {
    background: options.background ?? clip.cutout?.background ?? "keep",
  });
  const matte = asset.matte;
  if (matte && matte.status !== "failed") return;
  try {
    const updated = await client.media.cutOutPerson({ orgId, assetId: asset.id });
    // The editor sees the progress right away; the activity tray starts polling fast.
    queryClient.setQueryData(
      orpc.media.get.queryKey({ input: { orgId, assetId: asset.id } }),
      updated,
    );
    void queryClient.invalidateQueries({ queryKey: orpc.media.list.key() });
    void queryClient.invalidateQueries({ queryKey: orpc.activity.key() });
  } catch (error) {
    // Not started (limit, too long): the clip stays as it was.
    if (!matte) setClipCutout(store, ref, null);
    toast.error(errorMessage(error));
  }
}

/**
 * Puts top-level elements or clips of upper tracks behind the cut-out person
 * (onto a track below the person track) or in front of it again.
 */
export function setBehindCutout(store: EditorStore, ids: readonly string[], behind: boolean) {
  store.getState().update((draft) => placeBehindPerson(draft, ids, behind));
}

/** Whether a top-level element or a clip of an upper track lies behind the cut-out person. */
export function isBehindCutout(doc: SceneDocument, id: string): boolean {
  const clip = doc.timeline?.overlays?.find((item) => item.id === id);
  const track = clip ? clip.track : resolveElementTracks(doc).get(id);
  if (track === undefined) return Boolean(doc.elements[id]?.behindCutout);
  return isBehindPersonTrack(doc, track);
}

/** Whether the document cuts out a person anywhere (the "behind" option only matters then). */
export function hasPersonCutout(doc: SceneDocument): boolean {
  return cutoutTrack(doc.timeline) !== null;
}

/** Time range of a clip on the timeline. */
function clipRange(doc: SceneDocument, ref: ClipRef): { start: number; end: number } | null {
  if (ref.track === "overlay") {
    const clip = doc.timeline?.overlays?.find((item) => item.id === ref.id);
    return clip ? { start: clip.start, end: overlayClipEnd(clip) } : null;
  }
  const entry = layoutClips(doc.timeline).find((item) => item.clip.id === ref.id);
  return entry ? { start: entry.start, end: entry.end } : null;
}

/**
 * A big title behind the person, over the clip's whole length: the classic
 * "text behind the speaker" look. It is selected, so it can be typed over.
 */
export function addTitleBehindPerson(store: EditorStore, ref: ClipRef): string | null {
  const { doc, pageIndex } = store.getState();
  const range = clipRange(doc, ref);
  if (!range) return null;
  const { width, height } = doc.format;
  const unit = Math.min(width, height);
  const fontSize = Math.round(unit * 0.24);
  const element = createTextElement({
    text: "DEIN TITEL",
    name: "Titel hinter der Person",
    x: Math.round(width * 0.04),
    y: Math.round(height * (height > width ? 0.16 : 0.1)),
    width: Math.round(width * 0.92),
    style: {
      fontFamily: "Bebas Neue",
      fontSize,
      fontWeight: 400,
      lineHeight: 0.95,
      uppercase: true,
      color: "#ffffff",
      align: "center",
    },
    time: { start: Math.round(range.start), end: Math.round(range.end) },
  });
  store.getState().update((draft) => {
    insertElements(draft, pageIndex, [element]);
    placeBehindPerson(draft, [element.id], true);
  });
  store.getState().select([element.id]);
  return element.id;
}
